import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { FilterGroupOptions, FindGroupWithOwnerIDsBody, GroupApi } from 'api';
import { PARTNER_STATUS } from 'utils';

export const findGroup = createAsyncThunk(
  'group/findGroup',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const selectSupplierPartnerGroup = createAsyncThunk(
  'group/selectSupplierPartnerGroup',
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.selectSupplier(params);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const selectCustomerPartnerGroup = createAsyncThunk(
  'group/selectCustomerPartnerGroup',
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.selectCustomer(params);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneGroup = createAsyncThunk(
  'group/findOneGroup',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, option } = params;
      const response = await GroupApi.findOne(id, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findGroupForMenuList = createAsyncThunk(
  'group/findGroupForMenuList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findGroupByStatusForMenuList = createAsyncThunk(
  'group/findGroupByStatusForMenuList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findGroupByTypeForMenuList = createAsyncThunk(
  'group/findGroupByTypeForMenuList',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findGroupToSelect = createAsyncThunk(
  'group/findGroupToSelect',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createGroup = createAsyncThunk(
  'group/createGroup',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await GroupApi.create(body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateGroup = createAsyncThunk(
  'group/updateGroup',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await GroupApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeGroup = createAsyncThunk(
  'group/removeGroup',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await GroupApi.remove(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const filterGroup = createAsyncThunk(
  'group/filterGroup',
  async (option: FilterGroupOptions, { rejectWithValue }) => {
    try {
      const response = await GroupApi.filter(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findGroupWithOwnerIDs = createAsyncThunk(
  'group/findGroupWithOwnerIDs',
  async (body: FindGroupWithOwnerIDsBody, { rejectWithValue }) => {
    try {
      const response = await GroupApi.findWithOwnerIDs(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface IState {
  list: Array<any>;
  menuList: Array<any>;
  supplierGroup: Array<any>;
  customerGroup: Array<any>;
  isLoadedCustomerGroup?: boolean;
  isLoadedSupplierGroup?: boolean;
}

const initialState: IState = {
  list: [],
  menuList: [],
  supplierGroup: [],
  customerGroup: [],
  isLoadedCustomerGroup: false,
  isLoadedSupplierGroup: false,
};

const groupSlice = createSlice({
  name: 'group',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(selectSupplierPartnerGroup.fulfilled, (state, action) => {
      const { data } = action.payload;

      const formatData = data.map(item => {
        return {
          ...item,
          title: `${item.name} - ${item.owner?.displayName}`,
          name: `${item.name} - ${item.owner?.displayName}`,
          value: item.id,
        };
      });

      state.supplierGroup = formatData;

      state.isLoadedSupplierGroup = true;
    });
    builder.addCase(selectCustomerPartnerGroup.fulfilled, (state, action) => {
      const { data } = action.payload;

      const formatData = data.map(item => {
        return {
          ...item,
          title: `${item.name} - ${item.owner?.displayName}`,
          name: `${item.name} - ${item.owner?.displayName}`,
          value: item.id,
        };
      });

      state.customerGroup = formatData;

      state.isLoadedCustomerGroup = true;
    });
    builder
      .addCase(findGroup.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.list = newData;
      })
      .addCase(findGroupForMenuList.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.menuList = newData;
      })
      .addCase(findGroupByStatusForMenuList.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          const totalPartner = item?.partners?.filter(partner =>
            [PARTNER_STATUS.ACTIVATED, PARTNER_STATUS.DEACTIVATED].includes(
              partner.status
            )
          )?.length;

          return {
            ...item,
            key: item.id,
            value: item.id,
            totalPartner,
          };
        });

        const dataSorted = newData.sort(
          (item1, item2) => item2.totalPartner - item1.totalPartner
        );

        state.menuList = dataSorted;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.list = state.list.concat(data);
      });
  },
});

const { reducer: groupReducer } = groupSlice;

export default groupReducer;
